import * as MT from '../motornet/types';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = 20;

/**
 * La pagina da mostrare quando la lista si accorcia (es. dopo un'eliminazione
 * massiva sull'ultima pagina)
 */
export const pageAfterShrink = (
	page: number,
	pageSize: number,
	total: number
): number => Math.min(page, Math.max(1, Math.ceil(total / pageSize)));

export interface Range {
	from: string;
	to: string;
}

export interface VehicleFilterState {
	stato_corrente: string[];
	stato_disp: string[];
	marca: string;
	modello: string;
	allestimento: string;
	dealer: string;
	colore_esterno: string;
	colore_interno: string;
	trazione: string;
	carrozzeria: string;
	city: string;
	alimentazione: string;
	cambio: string;
	utente: string;
	search: string;
	data_disp: Range;
	data_immatricolazione: Range;
	offerta_minima: Range;
}

export interface RoleScope {
	quotable?: boolean;
	statoCorrente?: string[];
}

export interface PagingState {
	page: number;
	pageSize: number;
	sortBy?: MT.PlatformVehiclesSortBy;
	sortDirection?: 'asc' | 'desc';
}

export const EMPTY_FILTER_STATE: VehicleFilterState = {
	stato_corrente: [],
	stato_disp: [],
	marca: '',
	modello: '',
	allestimento: '',
	dealer: '',
	colore_esterno: '',
	colore_interno: '',
	trazione: '',
	carrozzeria: '',
	city: '',
	alimentazione: '',
	cambio: '',
	utente: '',
	search: '',
	data_disp: { from: '', to: '' },
	data_immatricolazione: { from: '', to: '' },
	offerta_minima: { from: '', to: '' },
};

const TEXT_KEYS = [
	'marca',
	'modello',
	'allestimento',
	'dealer',
	'colore_esterno',
	'colore_interno',
	'trazione',
	'carrozzeria',
	'city',
	'alimentazione',
	'cambio',
	'utente',
	'search',
] as const;

const RANGE_KEYS = [
	'data_disp',
	'data_immatricolazione',
	'offerta_minima',
] as const;

const SORT_FIELD_BY_COLUMN_ID: Record<string, MT.PlatformVehiclesSortBy> = {
	stato_corrente: 'stato_corrente.type',
	offerta_minima: 'offerta_minima.valore',
	year: 'model_year',
};

const SORTABLE_FIELDS = new Set<string>(MT.PLATFORM_VEHICLES_SORT_FIELDS);

export const toSortBy = (
	columnId?: string
): MT.PlatformVehiclesSortBy | undefined => {
	if (!columnId) return undefined;

	const field = SORT_FIELD_BY_COLUMN_ID[columnId] ?? columnId;

	return SORTABLE_FIELDS.has(field)
		? (field as MT.PlatformVehiclesSortBy)
		: undefined;
};

const text = (value: string | undefined) => {
	const trimmed = (value ?? '').trim();
	return trimmed === '' ? undefined : trimmed;
};

export const toVehiclesRequest = (
	filters: VehicleFilterState,
	scope: RoleScope,
	paging: PagingState
): MT.PlatformVehiclesRequest | null => {
	const request: MT.PlatformVehiclesRequest = {
		page: paging.page,
		pageSize: paging.pageSize,
		sortBy: paging.sortBy ?? 'disponibili',
		sortDirection: paging.sortDirection ?? (paging.sortBy ? 'desc' : 'asc'),
		normalize: true,
		extractModel: true,
	};

	const target = request as Record<string, unknown>;

	TEXT_KEYS.forEach((key) => {
		const value = text(filters[key]);
		if (value !== undefined) target[key] = value;
	});

	RANGE_KEYS.forEach((key) => {
		const from = text(filters[key]?.from);
		const to = text(filters[key]?.to);
		if (from !== undefined) target[`${key}_from`] = from;
		if (to !== undefined) target[`${key}_to`] = to;
	});

	if (filters.stato_disp.length) {
		request.stato_disp = filters.stato_disp;
	}

	if (scope.quotable) {
		request.quotable = true;
	}

	const chosen = filters.stato_corrente;
	const allowed = scope.statoCorrente;

	if (allowed) {
		const intersection = chosen.length
			? chosen.filter((state) => allowed.includes(state))
			: allowed;

		if (!intersection.length) return null;

		request.stato_corrente = intersection;
	} else if (chosen.length) {
		request.stato_corrente = chosen;
	}

	return request;
};
